import { useCallback, useEffect, useMemo, useState } from "react";
import { OrderType } from "../entities/interfaces/order";

export interface ServiceOrder {
  type: OrderType.SERVICE_ORDER;
  serviceItems: ServiceOrderServiceItem[];
  amount: number;
  membershipDiscount?: number;
}

export interface ServiceOrderServiceItem {
  serviceId: string;
  quantity: number;
  additionalCharges?: number;
}

export interface MembershipOrder {
  type: OrderType.MEMBERSHIP;
  amount: number;
}

export interface VoucherOrder {
  type: OrderType.VOUCHER;
  code: string;
  amount: number;
}

export type OrderItem = ServiceOrder | MembershipOrder | VoucherOrder;

export type OrderItems = OrderItem[];

// Overloads with Partial support
function useOrderItem() {
  const [orderItemMap, setOrderItemMap] = useState<Map<OrderType, OrderItem>>(
    new Map()
  );
  const [orderItems, setOrderItems] = useState<OrderItems>([]);

  // Overloads
  const addOrUpdateServiceOrderItem = useCallback(
    (data: Partial<Omit<ServiceOrder, "type">>) => {
      setOrderItemMap((prev) => {
        const type = OrderType.SERVICE_ORDER;
        const newMap = new Map(prev);
        const existing = newMap.get(type) || {};
        const updated = { ...existing, ...data, type } as ServiceOrder;
        newMap.set(type, updated);
        return newMap;
      });
    },
    []
  );

  const addOrUpdateMembershipItem = useCallback(
    (data: Partial<Omit<MembershipOrder, "type">>) => {
      setOrderItemMap((prev) => {
        const type = OrderType.MEMBERSHIP;
        const newMap = new Map(prev);
        const existing = newMap.get(type) || {};
        const updated = { ...existing, ...data, type } as MembershipOrder;
        newMap.set(type, updated);
        return newMap;
      });
    },
    []
  );

  const addOrUpdateVoucherItem = useCallback(
    (data: Partial<Omit<VoucherOrder, "type">>) => {
      setOrderItemMap((prev) => {
        const type = OrderType.VOUCHER;
        const newMap = new Map(prev);
        const existing = newMap.get(type) || {};
        const updated = { ...existing, ...data, type } as VoucherOrder;
        newMap.set(type, updated);
        return newMap;
      });
    },
    []
  );

  const removeOrderItem = useCallback((type: OrderType) => {
    setOrderItemMap((prev) => {
      const newMap = new Map(prev);
      newMap.delete(type);
      return newMap;
    });
  }, []);

  useEffect(() => {
    setOrderItems(() => Array.from(orderItemMap.values()));
  }, [orderItemMap]);

  return {
    orderItems,
    addOrUpdateServiceOrderItem,
    addOrUpdateMembershipItem,
    addOrUpdateVoucherItem,
    removeOrderItem,
  };
}

export default useOrderItem;
